import { useEffect, useState } from 'react';
import { api, darf, datum, uhr, type Anmelder } from '../api.ts';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Was von selbst läuft.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem neuen
 *    system."
 *
 * EIN EIGENER AUSLÖSER JE LAUF braucht Werkzeuge, um die Auslöser zu zählen,
 * aufzuräumen und zusammenzulegen - fünf Menüpunkte, die alle nur davon
 * handeln, dass es zu viele Auslöser gibt. Hier ist es eine Tabelle.
 *
 * WAS AUF DIESER SEITE ZU SEHEN SEIN MUSS, und zwar ohne Klick: läuft es
 * überhaupt, wann war jeder Lauf zuletzt dran, und was kam heraus. Ein
 * Bildschirm, der nur „aktiv" sagt, beantwortet die Frage nicht, die man
 * stellt, wenn eine Nachricht ausbleibt.
 */

export default function Automatik({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Record<string, any>>({});
  const darfSteuern = darf(ich, 'automatik.steuern');

  async function laden() { setD(await api.automatik()); }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  if (!d) return null;

  async function jetzt(s: string) {
    setArbeitet(s);
    try {
      const b = await api.automatikJetzt(s);
      setErgebnis((e) => ({ ...e, [s]: b }));
      await laden();
    } finally { setArbeitet(null); }
  }

  async function umschalten(s: string, aktiv: boolean) {
    setArbeitet(s);
    try { await api.automatikAktiv(s, aktiv); await laden(); }
    finally { setArbeitet(null); }
  }

  const aus = d.laeufe.filter((l: any) => !l.aktiv).length;
  const kaputt = d.laeufe.filter((l: any) => l.zuletzt_ok === false).length;
  /* WELCHER LAUF NOCH NIE GELAUFEN IST.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „genauso wie der Punkt mit den Automatiken. Ich weiß nicht, ob das
   *    alles so fertig ist."
   *
   * IN DER TABELLE STAND ES SCHON - „noch nie", in der Spalte „Zuletzt", in
   * einer von zwanzig Zeilen. Wer die Frage stellt, ob das FERTIG ist, liest
   * aber nicht zwanzig Zeilen durch, sondern sucht eine Zahl. Ein täglicher
   * Lauf, der nach acht Tagen noch nie ging, ist genau die Auskunft, die
   * gefehlt hat: entweder gab es nichts zu tun und er hat es nicht vermerkt,
   * oder er läuft nicht.
   *
   * Ein STÜNDLICHER Lauf, der nie ging, ist dabei etwas anderes als ein
   * WÖCHENTLICHER: der erste ist ein Befund, der zweite kann einfach noch
   * nicht dran gewesen sein. Deshalb steht der Takt daneben. */
  const nieGelaufen = d.laeufe.filter((l: any) => l.aktiv && !l.zuletzt_am);

  return (
    <>
      <div className="kopf">
        <h1>Automatik</h1>
        <span className="weg">Was von selbst läuft</span>
      </div>
      <div className="inhalt">

        {!d.an && (
          <div className="hinweis fehler">
            <b>Die Automatik ist abgeschaltet.</b>
            Der Hauptschalter <code>AUTOMATIK_AN</code> steht auf „nein". Kein
            einziger Lauf geht los, auch keiner, der unten als aktiv steht.
          </div>
        )}
        {d.an && !d.chat && (
          <div className="hinweis warnung">
            <b>Kein Telegram-Chat hinterlegt.</b>
            Die Läufe laufen, aber ihre Meldungen landen nur im
            Nachrichtenprotokoll und erreichen niemanden.
            Einzutragen unter <code>AUTOMATIK_CHAT</code> in der Konfiguration.
          </div>
        )}
        {kaputt > 0 && (
          <div className="hinweis fehler">
            <b>{kaputt} {kaputt === 1 ? 'Lauf ist' : 'Läufe sind'} zuletzt
            gescheitert.</b> Der Grund steht in der Zeile.
          </div>
        )}

        {nieGelaufen.length > 0 && (
          <div className="hinweis warnung">
            <b>
              {nieGelaufen.length === 1
                ? 'Ein eingeschalteter Lauf ist noch nie gelaufen'
                : `${nieGelaufen.length} eingeschaltete Läufe sind noch nie gelaufen`}
            </b>
            Das heißt nicht zwingend, dass etwas kaputt ist - ein
            wöchentlicher Lauf kann schlicht noch nicht dran gewesen sein.
            Bei einem stündlichen oder täglichen ist es einer.
            <div className="klein" style={{ marginTop: 5 }}>
              {nieGelaufen.map((l: any) => `${l.name} (${l.takt})`).join(', ')}
            </div>
          </div>
        )}

        {/* DIE ZAHLEN ZUERST, UND ZWAR FARBIG.
            ---------------------------------------------------------------
            ORIGINALTEXT MARVIN (01.09.2026)
              „kannst du bei den 3 letzten seiten das grau in grau anpassen.
               das wirkt so unsortiert. dynamischer und innovativer
               gestalten."

            Dieselbe Bauart wie in der Auswertung und in der Ortszuordnung:
            eine Kachel je Frage, die man beim Öffnen hat. Sie standen bisher
            als Halbsatz im Kartenkopf („20 insgesamt, 2 abgeschaltet, …") -
            in derselben Schrift wie alles andere, also unsichtbar. */}
        <div className="kachelreihe">
          <div className="kachel">
            <div className="wert" style={{ color: 'var(--erledigt)' }}>
              {d.laeufe.length - aus}
            </div>
            <div className="was">laufen</div>
          </div>
          <div className="kachel">
            <div className="wert">{aus}</div>
            <div className="was">abgeschaltet</div>
          </div>
          <div className="kachel">
            <div className="wert"
                 style={kaputt ? { color: 'var(--ueberfaellig)' } : undefined}>
              {kaputt}
            </div>
            <div className="was">zuletzt gescheitert</div>
          </div>
          <div className="kachel">
            <div className="wert"
                 style={nieGelaufen.length
                   ? { color: 'var(--neu)' } : undefined}>
              {nieGelaufen.length}
            </div>
            <div className="was">noch nie gelaufen</div>
          </div>
          <div className="kachel">
            <div className="wert">{Math.round(d.takt_ms / 1000)} s</div>
            <div className="was">Takt</div>
          </div>
        </div>

        <div className="karte">
          <h3>
            <span className={'zustandspunkt '
              + (kaputt ? 'hart' : nieGelaufen.length ? 'offen' : 'gut')} />
            Die Läufe
            <span className="weg">
              {d.laeufe.length} insgesamt
              {aus > 0 && `, ${aus} abgeschaltet`}
            </span>
          </h3>
          <div className="leib">
            <table className="liste">
              <thead>
                <tr>
                  <th>Lauf</th>
                  <th style={{ width: 150 }}>Wann</th>
                  <th style={{ width: 190 }}>Zuletzt</th>
                  <th style={{ width: 150 }}>Als nächstes</th>
                  <th style={{ width: 190 }}></th>
                </tr>
              </thead>
              <tbody>
                {/* KEINE DECKKRAFT MEHR. Hier stand `opacity: .55` an jeder
                    abgeschalteten Zeile - derselbe Fehler wie in der
                    Rechtemaske: eine halbdurchsichtige Schrift ist keine
                    Auskunft, sondern eine schlechter lesbare. Der Zustand
                    steht jetzt als Punkt vor dem Namen und als Wort in der
                    Spalte daneben. */}
                {d.laeufe.map((l: any) => (
                  <tr key={l.schluessel} style={{ cursor: 'default' }}>
                    <td>
                      <span className={'zustandspunkt '
                        + (!l.aktiv ? 'aus'
                           : l.zuletzt_ok === false ? 'hart'
                           : !l.zuletzt_am ? 'offen' : 'gut')}
                        style={{ display: 'inline-block', marginRight: 8 }} />
                      <b>{l.name}</b>
                      <div className="klein leise">{l.zweck}</div>
                      {ergebnis[l.schluessel] && (
                        <div className={'klein ' + (ergebnis[l.schluessel].ok
                          ? '' : 'rot')}>
                          Eben von Hand: {ergebnis[l.schluessel].meldung}
                        </div>
                      )}
                    </td>
                    <td className="klein">{l.takt_text ?? taktText(l)}</td>
                    <td className="klein">
                      {l.zuletzt_am ? (
                        <>
                          <span className={'marke-zustand z-'
                            + (l.zuletzt_ok === false ? 'ueberfaellig' : 'erledigt')}>
                            {l.zuletzt_ok === false ? 'gescheitert' : 'gelaufen'}
                          </span>
                          <div className="leise" style={{ fontSize: 11 }}>
                            {datum(l.zuletzt_am)} {uhr(l.zuletzt_am)}
                            {l.zuletzt_dauer_ms != null
                              && `, ${l.zuletzt_dauer_ms} ms`}
                          </div>
                          <div style={{ fontSize: 11 }}>{l.zuletzt_meldung}</div>
                        </>
                      ) : <span className="leise">noch nie</span>}
                    </td>
                    <td className="klein leise">
                      {l.aktiv
                        ? (l.naechste
                            ? `${datum(l.naechste)} ${uhr(l.naechste)}`
                            : 'unbekannt')
                        : <span className="marke-zustand z-neu">
                            abgeschaltet
                          </span>}
                      {l.fehler_woche > 0 && (
                        <div className="rot" style={{ fontSize: 11 }}>
                          {l.fehler_woche} Fehler in 7 Tagen
                        </div>
                      )}
                    </td>
                    <td>
                      {darfSteuern && (
                        <div className="reihe">
                          <button className="knopf klein"
                                  disabled={arbeitet === l.schluessel}
                                  onClick={() => jetzt(l.schluessel)}>
                            {arbeitet === l.schluessel ? '…' : 'Jetzt'}
                          </button>
                          <button className="knopf klein"
                                  disabled={arbeitet === l.schluessel}
                                  onClick={() => umschalten(l.schluessel, !l.aktiv)}>
                            {l.aktiv ? 'Abschalten' : 'Anschalten'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {darf(ich, 'konfiguration.pflegen') && <Meldewege />}

        {/* Die Läufe der letzten Tage. „Seit wann geht das schon so?" ist die
            erste Frage, wenn etwas nicht ankommt, und mit einem einzigen Stand
            lässt sie sich nicht beantworten. */}
        <div className="karte">
          <h3>Die letzten Durchgänge</h3>
          <div className="leib">
            <table className="liste">
              <thead>
                <tr>
                  <th style={{ width: 170 }}>Wann</th>
                  <th style={{ width: 200 }}>Lauf</th>
                  <th style={{ width: 90 }}>Gefunden</th>
                  <th style={{ width: 90 }}>Gemeldet</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {d.letzte.map((l: any, i: number) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td className="klein leise">
                      {datum(l.begonnen_am)} {uhr(l.begonnen_am)}
                      {l.von_hand && <div style={{ fontSize: 11 }}>von Hand</div>}
                    </td>
                    <td className="klein">
                      {d.laeufe.find((x: any) => x.schluessel === l.schluessel)
                        ?.name ?? l.schluessel}
                    </td>
                    <td className="klein">{l.gefunden}</td>
                    <td className="klein">{l.gemeldet}</td>
                    <td className={'klein' + (l.erfolgreich === false ? ' rot' : '')}>
                      {l.meldung}
                    </td>
                  </tr>
                ))}
                {!d.letzte.length && (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, textAlign: 'center' }}>
                      <b>Noch kein Durchgang.</b>
                      <div className="leise klein">
                        Der Takt sieht jede Minute nach. Was heute schon fällig
                        war, holt er beim nächsten Blick nach.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Wohin die Meldungen gehen, und ob der Rückweg steht.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „Was ist mit den telegram Benachrichtigungen in den gruppen?"
 *
 * SEIN PLURAL WAR DER PUNKT. Bis Migration 097 ging jede Meldung in denselben
 * Chat; früher waren es achtzehn Themen. Zwanzig Läufe in einer Liste
 * sind eine Liste, die niemand mehr liest.
 *
 * ES STEHT HIER UND NICHT IN DER KONFIGURATION, weil hier die Frage entsteht:
 * wer auf dieser Seite steht, sieht gerade die Läufe und will wissen, wo
 * deren Meldungen ankommen. Zwei Wege zu derselben Sache wären wieder eine
 * halbe Auskunft.
 */
function Meldewege() {
  const [d, setD] = useState<any>(null);
  const [offen, setOffen] = useState(false);
  const [hook, setHook] = useState<any>(null);
  const [laedt, setLaedt] = useState(false);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  /* DIE SAMMELZUTEILUNG. Welche Kennung ist gewählt, und welche Anlässe
     sollen sie bekommen. Sie steht ausserhalb der Tabelle, weil sie eine
     ANDERE Frage beantwortet: nicht „wohin meldet dieser Lauf", sondern
     „welche Läufe melden in diese Gruppe". */
  const [gewaehlt, setGewaehlt] = useState<string>('');
  const [ziele, setZiele] = useState<string[]>([]);

  async function laden() { setD(await api.telegramAnlaesse()); }
  useEffect(() => { if (offen && !d) laden(); }, [offen]);
  /* NACHGEZOGEN WIRD NUR, WAS JEMAND ANSIEHT. Die Liste hängt hinter einem
     zugeklappten Bereich; ein Nachzug im Zugeklappten wäre eine Anfrage für
     einen Bildschirm, den niemand vor sich hat. */
  useFrisch(() => { if (offen) laden(); });

  async function speichern(anlass: string, wert: string) {
    try {
      await api.telegramAnlass(anlass, wert);
      setEntwurf((e) => { const n = { ...e }; delete n[anlass]; return n; });
      setMeldung(null);
      await laden();
    } catch (f: any) {
      setMeldung(f?.hinweis ?? f?.fehler ?? String(f));
    }
  }

  /* ALLES ODER NICHTS, und der Server hält es genauso: steht ein Anlass in
     der Liste, den es nicht gibt, wird gar nichts geschrieben. */
  async function sammelZuteilen() {
    try {
      const a = await api.telegramAnlaesseSetzen(gewaehlt, ziele);
      setMeldung(`${a.geaendert} ${a.geaendert === 1 ? 'Anlass meldet' : 'Anlässe melden'} `
                 + `jetzt ${gewaehlt ? 'nach ' + gewaehlt : 'in die Backoffice-Gruppe'}.`);
      setZiele([]);
      await laden();
    } catch (f: any) {
      setMeldung(f?.hinweis ?? f?.fehler ?? String(f));
    }
  }

  /* DER HAKEN „PER MAIL" (21.09.2026). Er schickt den Chat unverändert mit:
     der PUT-Weg schreibt `chat_id` immer, und wer hier nur den Haken setzt,
     darf dabei nicht den Chat verlieren. */
  /* „AN PERSONEN" (.420). Marvin, 20.10.2026: „Diese Meldung muss nicht
     zentral ins Backoffice kommen, sondern die kann an mich kommen als
     Administrator." Mit dem Haken geht der Anlass an jeden Zugang mit dem
     Recht „Betriebsmeldungen per Telegram bekommen", in keinen Chat. */
  async function personenUmschalten(anlass: string, an: boolean) {
    const jetzt = d.anlaesse.find((a: any) => a.anlass === anlass);
    await api.telegramAnlass(anlass, jetzt?.chat_id ?? '', undefined, undefined, an);
    await laden();
  }

  async function mailUmschalten(anlass: string, perMail: boolean) {
    const jetzt = d.anlaesse.find((a: any) => a.anlass === anlass);
    await api.telegramAnlass(anlass, jetzt?.chat_id ?? '', undefined, perMail);
    await laden();
  }

  async function umschalten(anlass: string, aktiv: boolean) {
    const jetzt = d.anlaesse.find((a: any) => a.anlass === anlass);
    await api.telegramAnlass(anlass, jetzt?.chat_id ?? '', aktiv);
    await laden();
  }

  async function hookHolen() {
    setLaedt(true);
    try { setHook(await api.telegramWebhook()); } finally { setLaedt(false); }
  }

  async function hookSetzen(uebernehmen: boolean) {
    setLaedt(true);
    setMeldung(null);
    try {
      const a = await api.telegramWebhookSetzen(uebernehmen);
      setMeldung(`Der Webhook zeigt jetzt auf ${a.adresse}.`);
      await hookHolen();
    } catch (f: any) {
      setMeldung([f?.fehler, f?.steht ? `Dort steht: ${f.steht}` : null,
                  f?.hinweis].filter(Boolean).join(' '));
    } finally { setLaedt(false); }
  }

  if (!offen) {
    return (
      <div className="karte">
        <h3>
          Wohin die Meldungen gehen
          <span className="weg">
            <button className="knopf klein" onClick={() => setOffen(true)}>
              Aufklappen
            </button>
          </span>
        </h3>
      </div>
    );
  }
  if (!d) return <div className="karte"><h3>Wohin die Meldungen gehen</h3></div>;

  const eigene = d.anlaesse.filter((a: any) => (a.chat_id ?? '').trim()).length;

  return (
    <div className="karte">
      <h3>
        <span className={'zustandspunkt ' + (d.standard ? 'gut' : 'offen')} />
        Wohin die Meldungen gehen
        <span className="weg">
          {eigene} von {d.anlaesse.length} mit eigenem Thema
          {', '}
          {d.anlaesse.filter((a: any) => a.per_mail).length} per Mail
          {', '}
          <button className="knopf klein" onClick={() => setOffen(false)}>
            Zuklappen
          </button>
        </span>
      </h3>
      <div className="leib">
        <p className="klein leise">
          Leer heisst: die Meldung geht in die Backoffice-Gruppe
          (<code>AUTOMATIK_CHAT</code>
          {d.standard ? <> = <code>{d.standard}</code></> : ', noch nicht gesetzt'}).
          Eine Gruppe mit Themen bekommt die Nummer des Themas mit Doppelpunkt
          dahinter, zum Beispiel <code>-1003969949096:25</code>.
        </p>
        {/* ZWEI FRAGEN, ZWEI SPALTEN, UND SIE SIND NICHT DASSELBE.
            Marvin, 21.09.2026: „ich will diese meldungen doch garnicht alle
            per mail erhalten. nur den wochenbericht." */}
        <p className="klein leise">
          <b>Per Mail</b> ist ein eigener Haken und nicht dasselbe wie der
          Chat: <b>welche</b> Meldung per Mail geht, steht hier je Anlass,
          <b> wohin</b> sie geht, unter Konfiguration in
          {' '}<code>automatik.mail_an</code>. Ab Werk trägt ihn nur die
          wöchentliche Auswertung, weil die nicht nach Telegram meldet. Ist
          {' '}<code>automatik.mail_an</code> leer, geht auch mit Haken keine
          Mail hinaus, und der Grund steht im Nachrichtenprotokoll.
        </p>

        {meldung && <div className="hinweis warnung">{meldung}</div>}

        <table className="liste">
          <thead>
            <tr>
              <th>Anlass</th>
              <th style={{ width: 260 }}>Chat oder Thema</th>
              {/* DER MAILWEG STEHT NEBEN DEM CHAT UND NICHT AN SEINER
                  STELLE. Marvin, 21.09.2026: „ich will diese meldungen doch
                  garnicht alle per mail erhalten. nur den wochenbericht."
                  Ein Anlass kann in den Chat, in die Mail, in beides oder in
                  keines von beiden. */}
              <th style={{ width: 110 }}>per Mail</th>
              <th style={{ width: 120 }} title={'Statt in einen Chat an die Telegram-Kennung jedes Zugangs mit dem Recht „Betriebsmeldungen per Telegram bekommen"'}>an Personen</th>
              <th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {d.anlaesse.map((a: any) => {
              const wert = entwurf[a.anlass] ?? (a.chat_id ?? '');
              const geaendert = wert !== (a.chat_id ?? '');
              return (
                <tr key={a.anlass} style={{ cursor: 'default' }}>
                  <td>
                    <span className={'zustandspunkt '
                      + (!a.aktiv ? 'aus' : a.chat_id ? 'gut' : 'offen')}
                      style={{ display: 'inline-block', marginRight: 8 }} />
                    <b>{a.titel}</b>
                    <div className="klein leise">
                      <code>{a.anlass}</code>
                      {!a.ist_lauf && ', kein Lauf, entsteht an einer Handlung'}
                    </div>
                  </td>
                  <td>
                    <input value={wert}
                           placeholder="leer = Backoffice-Gruppe"
                           onChange={(e) => setEntwurf(
                             (x) => ({ ...x, [a.anlass]: e.target.value }))} />
                    {/* EINE KENNUNG ZUM ABSCHREIBEN IST KEINE ZUORDNUNG.
                        Hier stand nur das Feld, und die achtzehn Kennungen
                        standen in einer Tabelle darunter: markieren, tippen,
                        und beim vierten Mal vertippt man sich. Das Feld
                        bleibt - es gibt Chats, die in keiner Spur stehen -,
                        aber der Regelfall ist jetzt ein Klick. */}
                    {d.gefundene_kennungen?.length > 0 && (
                      <select
                        className="klein" style={{ marginTop: 5, width: '100%' }}
                        value=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          setEntwurf((x) => ({ ...x, [a.anlass]: e.target.value }));
                        }}>
                        <option value="">aus dem Protokoll übernehmen …</option>
                        {d.gefundene_kennungen.map((b: any) => (
                          <option key={b.schluessel} value={b.chat_id}>
                            {b.name}, {b.chat_id}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <label className="klein"
                           style={{ display: 'flex', gap: 6,
                                    alignItems: 'center' }}>
                      <input type="checkbox" checked={!!a.per_mail}
                             onChange={() => mailUmschalten(a.anlass,
                                                            !a.per_mail)} />
                      {a.per_mail ? 'ja' : 'nein'}
                    </label>
                  </td>
                  <td>
                    <label className="klein"
                           style={{ display: 'flex', gap: 6,
                                    alignItems: 'center' }}>
                      <input type="checkbox" checked={!!a.an_personen}
                             onChange={() => personenUmschalten(a.anlass,
                                                                !a.an_personen)} />
                      {a.an_personen ? 'ja, kein Chat' : 'nein'}
                    </label>
                  </td>
                  <td className="knopfleiste">
                    {geaendert && (
                      <button className="knopf klein haupt"
                              onClick={() => speichern(a.anlass, wert)}>
                        Speichern
                      </button>
                    )}
                    {/* MELDET NICHT ist etwas anderes als ein leeres Feld und
                        etwas anderes als ein abgeschalteter Lauf: der Lauf
                        läuft weiter, schreibt seinen Befund, und der Mailweg
                        der Automatik bleibt unberührt. */}
                    <button className="knopf klein"
                            onClick={() => umschalten(a.anlass, !a.aktiv)}>
                      {a.aktiv ? 'Meldet nicht' : 'Meldet wieder'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* EINE KENNUNG, MEHRERE ANLÄSSE.
            ---------------------------------------------------------------
            ORIGINALTEXT MARVIN (02.09.2026)
              „die telegram kennungen werden gebracuht und müssen auch wieder
               richtig verknüpft werden in der plattform, damit die jeweilige
               gruppe den rihtigen push erhält"

            EIN THEMA NIMMT SELTEN GENAU EINEN ANLASS AUF. „Termine" bekommt
            die Vorabend-Info, die Verschiebung und die Absage; einzeln
            zugeteilt sind das drei Klicks und drei Gelegenheiten, eine Ziffer
            zu verlieren.

            ZUGEORDNET WIRD TROTZDEM VON HAND. Ein Name wie „VAO" passt auf
            zwei unserer Läufe, andere auf keinen - und eine geratene
            Zuordnung schickt die Meldung in ein Thema, in dem sie niemand
            erwartet. Das System macht den Weg kurz, nicht die Entscheidung. */}
        {d.gefundene_kennungen?.length > 0 && (
          <>
            <h4 style={{ marginTop: 18 }}>Eine Kennung mehreren Anlässen geben</h4>
            <p className="klein leise">
              Diese {d.gefundene_kennungen.length} Chat-Kennungen stehen im
              Änderungsprotokoll: beim Aufräumen der Konfiguration sind sie
              nicht weggeworfen, sondern in die Spur geschrieben worden. Es
              sind Telegram-Gruppen und -Themen, die es weiterhin gibt.
              <b> Welcher Lauf in welches Thema meldet, weiß niemand außer
              Ihnen</b> - deshalb wird hier nichts vorbelegt.
            </p>

            <div className="feldreihe">
              <label>
                Kennung
                <select value={gewaehlt}
                        onChange={(e) => setGewaehlt(e.target.value)}>
                  <option value="">(leer) = Backoffice-Gruppe</option>
                  {d.gefundene_kennungen.map((b: any) => (
                    <option key={b.schluessel} value={b.chat_id}>
                      {b.name}, {b.chat_id}
                      {b.benutzt_von.length
                        ? `, schon bei ${b.benutzt_von.length}`
                        : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* WER SIE SCHON BENUTZT. Ohne diese Zeile weiss niemand, ob eine
                Kennung noch frei ist - und teilt sie ein zweites Mal zu. */}
            {gewaehlt && (() => {
              const k = d.gefundene_kennungen.find((b: any) => b.chat_id === gewaehlt);
              return (
                <p className="klein leise" style={{ marginTop: 4 }}>
                  {k?.benutzt_von?.length
                    ? <>Meldet heute schon hierher: {k.benutzt_von.join(', ')}.</>
                    : <>Diese Kennung ist noch keinem Anlass zugeteilt.</>}
                </p>
              );
            })()}

            <div className="reihe" style={{ marginTop: 8, flexWrap: 'wrap' }}>
              {d.anlaesse.map((a: any) => (
                <label key={a.anlass} className="klein"
                       style={{ display: 'flex', alignItems: 'center',
                                gap: 6, minWidth: 210 }}>
                  <input type="checkbox"
                         checked={ziele.includes(a.anlass)}
                         onChange={(e) => setZiele((z) => e.target.checked
                           ? [...z, a.anlass] : z.filter((x) => x !== a.anlass))} />
                  {a.titel}
                </label>
              ))}
            </div>

            {/* AUCH HIER EINE ZEILE UND KEIN RASTER. Hier stand
                `knopfleiste`, also ein Raster aus 88-Pixel-Spalten: die
                Leiste ist 1276 Pixel breit, das ergibt DREIZEHN gleich breite
                Spalten fuer EINEN Knopf, und „Anlässe wählen" bekam 93 Pixel
                fuer 119 Pixel Aufschrift. Mit einer Auswahl heisst er
                „18 Anlässe zuteilen" und braucht noch mehr. Nachgemessen im
                Browser bei 1630, 1304 und 900 Pixeln: 26, 29 und 15 Pixel
                Ueberstand. Ein Raster ist richtig, wo Kanten UNTEREINANDER
                stehen sollen; eine Werkzeugleiste ist eine ZEILE. */}
            <div className="reihe" style={{ marginTop: 10 }}>
              <button className="knopf haupt"
                      disabled={!ziele.length}
                      onClick={sammelZuteilen}>
                {ziele.length
                  ? `${ziele.length} ${ziele.length === 1 ? 'Anlass' : 'Anlässe'} zuteilen`
                  : 'Anlässe wählen'}
              </button>
              {ziele.length > 0 && (
                <button className="knopf" onClick={() => setZiele([])}>
                  Auswahl leeren
                </button>
              )}
            </div>
          </>
        )}

        {/* DER RÜCKWEG, UND DER RIEGEL DAVOR. */}
        <h4 style={{ marginTop: 18 }}>Der Rückweg von Telegram</h4>
        <p className="klein leise">
          Damit ein Knopf unter einer Meldung etwas auslösen kann, muss
          Telegram wissen, wohin es den Knopfdruck schickt.
          <b> Ein Bot hat dafür genau eine Adresse.</b> Zeigt sie heute auf
          eine andere Anwendung, nimmt ein Umstellen dieser Anwendung jede
          eingehende Nachricht weg - lautlos, denn das Senden funktioniert
          weiter.
        </p>
        <div className="reihe">
          <button className="knopf klein" disabled={laedt} onClick={hookHolen}>
            {laedt ? '…' : 'Nachsehen, was eingetragen ist'}
          </button>
        </div>
        {hook && (
          <div className={'hinweis ' + (hook.fremd ? 'fehler'
                            : hook.adresse ? '' : 'warnung')}
               style={{ marginTop: 10 }}>
            <b>{hook.satz}</b>
            {hook.adresse && (
              <div className="klein">
                Eingetragen: <code>{hook.adresse}</code>
              </div>
            )}
            <div className="klein">Hier wäre: <code>{hook.unsere}</code></div>
            {hook.fehler && (
              <div className="klein rot">Telegram meldet: {hook.fehler}</div>
            )}
            {hook.wartend != null && hook.wartend > 0 && (
              <div className="klein">
                {hook.wartend} Nachricht(en) warten bei Telegram.
              </div>
            )}
            {hook.ok && hook.adresse !== hook.unsere && (
              <div className="reihe" style={{ marginTop: 8 }}>
                <button className="knopf klein"
                        disabled={laedt}
                        onClick={() => hookSetzen(hook.fremd)}>
                  {hook.fremd
                    ? 'Trotzdem hierher umstellen'
                    : 'Auf diese Anwendung eintragen'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Der Takt in einem Satz. Dieselben Wörter wie im Server. */
function taktText(l: any): string {
  const wt = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag',
              'Samstag', 'Sonntag'];
  switch (l.takt) {
    case 'stuendlich': return 'stündlich';
    case 'taeglich': return `täglich um ${l.uhrzeit} Uhr`;
    case 'werktaeglich': return `an Werktagen um ${l.uhrzeit} Uhr`;
    case 'woechentlich': return `${wt[(l.wochentag ?? 1) - 1]}s um ${l.uhrzeit} Uhr`;
    default: return l.takt;
  }
}
